import { ReactNode } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { useShiftsListStore } from '@libs/store/shifts';
import { Shift } from '@libs/models/shifts';
import { ShiftTypeInline } from '@organisms/dataDisplay';
import { InfoBlocks, ShiftRecurrenceChip } from '@atoms/dataDisplay';
import { Flex, PrimaryPaper } from '@atoms/layout';
import { Box, Stack } from '@mui/material';
import { ShiftAssigneeButton } from '../singleShiftAssigneeButton';
import { useRefreshSyncStore } from '@libs/store/refreshSync';

export function GeneralInfo() {
  const { currentShift } = useShiftsListStore();
  const { callRefresh } = useRefreshSyncStore();

  if (!currentShift) {
    return null;
  }
  const isRecurrencePart = currentShift.recurrence !== 'single';
  const isSingleAssignee = isRecurrencePart && !currentShift.allowedMultipleAssignees;

  return (
    <Stack direction="column" spacing={1}>
      {!isSingleAssignee && (
        <>
          <Typography variant="subtitle1">
            <FormattedMessage id="shifts.fields.assignee" />
          </Typography>

          <PrimaryPaper>
            <Panel>
              <Box sx={{ height: '56px', width: "100%", p: 0}}>
                <ShiftAssigneeButton
                  shift={{
                    ...currentShift,
                    assignees: currentShift.assignee
                      ? [{ ...currentShift.assignee, id: currentShift.assignee.name }]
                      : [],
                  }}
                  assignButtonVariant="center"
                  onAssigned={callRefresh}
                  onUnassigned={callRefresh}
                  disabled={['NotAllowed', 'Canceled'].includes(currentShift.status)}
                  assignDialogProps={{
                    warningMessage: !currentShift.allowedMultipleAssignees && (
                      <FormattedMessage id="shifts.assignDialog.singleAssigneeDisclaimer" />
                    ),
                  }}
                  unassignWarningMessage={
                    !currentShift.allowedMultipleAssignees && (
                      <FormattedMessage id="shifts.unassignDialog.singleUnassignDisclaimer" />
                    )
                  }
                />
              </Box>
            </Panel>
          </PrimaryPaper>
        </>
      )}
      <Typography variant="subtitle1">
        <FormattedMessage id="shifts.drawers.shiftDetails.generalInfo.tags.heading" />
      </Typography>
      <PrimaryPaper>
        <Panel>
          <Stack direction={'row'}>
            {!currentShift.tags.length && <Typography variant="body2">No tags</Typography>}
            {currentShift.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Stack>
        </Panel>
      </PrimaryPaper>

      <ShiftDetailsInfoBlock shift={currentShift} />

      <LocationDetailsInfoBlock shift={currentShift} />

      {/* <SchedulerInfoBlock
        title={<FormattedMessage id="shifts.mainScheduler" />}
        scheduler={currentShift.mainScheduler}
      /> */}

      {/* <SchedulerInfoBlock
        title={<FormattedMessage id="shifts.afterHoursScheduler" />}
        scheduler={currentShift.afterHoursScheduler}
      /> */}
    </Stack>
  );
}

function ShiftDetailsInfoBlock({ shift }: { shift: Shift }) {
  return (
    <InfoBlocks
      title={<FormattedMessage id="shifts.shiftDetails" />}
      items={[
        {
          key: 'recurrence',
          title: <FormattedMessage id="shifts.fields.recurrence" />,
          value: (
            <ShiftRecurrenceChip
              variant="outlined"
              type={shift.recurrence}
              label={<FormattedMessage id={`shifts.recurrence.${shift.recurrence}`} />}
              size="small"
            />
          ),
        },
        {
          key: 'shiftType',
          title: <FormattedMessage id="shifts.fields.shiftType" />,
          value: <ShiftTypeInline startTime={dayjs(shift.shiftStart)} length={shift.shiftLength} />,
        },
        {
          key: 'qualification',
          title: <FormattedMessage id="talents.qualification" />,
          value: (
            <Flex gap={1} flexWrap={'wrap'}>
              {shift.qualifications.map((q) => (
                <Chip key={q.id} label={q.abbreviation} title={q.name} size="small" />
              ))}
            </Flex>
          ),
        },
        {
          key: 'shiftDate',
          title: <FormattedMessage id="shifts.fields.shiftDate" />,
          value: <FormattedDate value={dayjs(shift.shiftStart).toDate()} />,
        },
        {
          key: 'specialty',
          title: <FormattedMessage id="shifts.fields.specialty" />,
          value: <Typography variant="body2">{shift.specialty ?? '-'}</Typography>,
        },
      ]}
    />
  );
}

function LocationDetailsInfoBlock({ shift }: { shift: Shift }) {
  return (
    <>
      <InfoBlocks
        title={<FormattedMessage id="shifts.locationDetails" />}
        items={[
          {
            key: 'locationName',
            title: <FormattedMessage id="shifts.fields.locationName" />,
            value: <Typography>{shift.location.name}</Typography>,
          },
          {
            key: 'address',
            title: <FormattedMessage id="shifts.fields.address" />,
            value: (
              <Typography>{[shift.location.address1, shift.location.address2].filter(Boolean).join(' ')}</Typography>
            ),
          },
          // {
          //   key: 'department',
          //   title: <FormattedMessage id="shifts.fields.department" />,
          //   value: <Typography>{shift.locationDetails?.department || '-'}</Typography>,
          // },
          // {
          //   key: 'subDepartment',
          //   title: <FormattedMessage id="shifts.fields.subDepartment" />,
          //   value: <Typography>{shift.locationDetails?.subDepartment || '-'}</Typography>,
          // },
          {
            key: 'primaryPhoneNumber',
            title: <FormattedMessage id="fields.primaryPhoneNumber" />,
            value: <Typography>{shift.locationDetails?.primaryPhoneNumber || '-'}</Typography>,
          },
          {
            key: 'secondaryPhoneNumber',
            title: <FormattedMessage id="fields.secondaryPhoneNumber" />,
            value: <Typography>{shift.locationDetails?.secondaryPhoneNumber || '-'}</Typography>,
          },
        ]}
      />
    </>
  );
}

function SchedulerInfoBlock({
  scheduler,
  title,
}: {
  scheduler?: {
    name: string;
    jobTitle: string;
    primaryPhoneNumber: string;
    email: string;
  };
  title: ReactNode;
}) {
  return (
    <>
      <InfoBlocks
        title={title}
        items={[
          {
            key: 'name',
            title: <FormattedMessage id="common.name" />,
            value: <Typography>{scheduler?.name ?? '-'}</Typography>,
          },
          {
            key: 'jobTitle',
            title: <FormattedMessage id="fields.jobTitle" />,
            value: <Typography>{scheduler?.jobTitle ?? '-'}</Typography>,
          },
          {
            key: 'primaryPhoneNumber',
            title: <FormattedMessage id="fields.primaryPhoneNumber" />,
            value: <Typography>{scheduler?.primaryPhoneNumber ?? '-'}</Typography>,
          },
          {
            key: 'email',
            title: <FormattedMessage id="fields.email" />,
            value: <Typography>{scheduler?.email ?? '-'}</Typography>,
          },
        ]}
      />
    </>
  );
}

const Panel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));
